import { handleUrl, myHeadersWithTokenContentType } from '../../global';
import {
  CityLocationResponse,
  CoverageDetail,
  NoticeCoverageData,
  PairWaitlistResponse,
  PoleGroupLimitsResponse,
  PoleLimitsListResponse,
  SingleCoverageData,
  UpdatePoleGroupLimitsData,
  UpdatePoleGroupLimitsResponse,
  WaitlistOfferData,
  WaitlistOfferResponse,
} from './types';

export const createNoticeCoverage = (
  id: number,
  data: NoticeCoverageData
): Promise<CoverageDetail> => {
  const requestOptions = {
    method: 'POST',
    headers: myHeadersWithTokenContentType(),
    body: JSON.stringify(data),
  };
  const baseUrl = handleUrl();
  return fetch(
    `${baseUrl}/api/V1/public-notices/${id}/notice-coverages`,
    requestOptions
  )
    .then((response) => response.json())
    .catch((error) => new Error(error));
};

export const getNoticeCoverage = (id: number): Promise<CoverageDetail> => {
  const requestOptions = {
    method: 'GET',
    headers: myHeadersWithTokenContentType(),
  };
  const baseUrl = handleUrl();
  return fetch(
    `${baseUrl}/api/V1/public-notices/${id}/notice-coverages`,
    requestOptions
  )
    .then((response) => response.json())
    .catch((error) => new Error(error));
};

export const createSingleCoverage = (
  id: number,
  data: SingleCoverageData
): Promise<CoverageDetail> => {
  const requestOptions = {
    method: 'POST',
    headers: myHeadersWithTokenContentType(),
    body: JSON.stringify(data),
  };
  const baseUrl = handleUrl();
  return fetch(
    `${baseUrl}/api/V1/public-notices/${id}/notice-coverages/single`,
    requestOptions
  )
    .then((response) => response.json())
    .catch((error) => new Error(error));
};

export const deleteCoverage = (id: number): Promise<CoverageDetail> => {
  const requestOptions = {
    method: 'DELETE',
    headers: myHeadersWithTokenContentType(),
  };
  const baseUrl = handleUrl();
  return fetch(`${baseUrl}/api/V1/notice-coverages/${id}`, requestOptions)
    .then((response) => response.json())
    .catch((error) => new Error(error));
};

export const getPoleLimits = (
  id: number,
  page: number,
  size: number
): Promise<PoleLimitsListResponse> => {
  const requestOptions = {
    method: 'GET',
    headers: myHeadersWithTokenContentType(),
  };
  const baseUrl = handleUrl();
  return fetch(
    `${baseUrl}/api/V1/public-notices/${id}/poles?page=${page}&size=${size}`,
    requestOptions
  )
    .then((response) => response.json())
    .catch((error) => new Error(error));
};

/**
 * Pair (pole × criteria group) capacity of one pole — `pole_id` is required:
 * notices carry hundreds of poles, so the screen opens pole by pole. A pair
 * without a configured row comes back with the backend default (PROT-2950).
 */
export const getPoleGroupLimits = (
  id: number,
  poleId: number
): Promise<PoleGroupLimitsResponse> => {
  const requestOptions = {
    method: 'GET',
    headers: myHeadersWithTokenContentType(),
  };
  const baseUrl = handleUrl();
  return fetch(
    `${baseUrl}/api/V1/public-notices/${id}/pole-group-limits?pole_id=${poleId}`,
    requestOptions
  )
    .then((response) => response.json())
    .catch((error) => new Error(error));
};

/**
 * Replaces the removed PATCH /poles/limits: limits are now per pole × group
 * pair. 400 when the limit falls below the pair's active occupants, the
 * pole is not a root pole, or the pole/group belongs to another notice.
 */
export const updatePoleGroupLimits = (
  id: number,
  data: UpdatePoleGroupLimitsData
): Promise<UpdatePoleGroupLimitsResponse> => {
  const requestOptions = {
    method: 'PATCH',
    headers: myHeadersWithTokenContentType(),
    body: JSON.stringify(data),
  };
  const baseUrl = handleUrl();
  return fetch(
    `${baseUrl}/api/V1/public-notices/${id}/pole-group-limits`,
    requestOptions
  )
    .then((response) => response.json())
    .catch((error) => new Error(error));
};

/** Ordered waitlist of one pair — includes capacity and pending offers. */
export const getPairWaitlist = (
  id: number,
  poleId: number,
  criteriaGroupId: number
): Promise<PairWaitlistResponse> => {
  const requestOptions = {
    method: 'GET',
    headers: myHeadersWithTokenContentType(),
  };
  const params = new URLSearchParams({
    pole_id: String(poleId),
    criteria_group_id: String(criteriaGroupId),
  });
  const baseUrl = handleUrl();
  return fetch(
    `${baseUrl}/api/V1/public-notices/${id}/waitlist?${params.toString()}`,
    requestOptions
  )
    .then((response) => response.json())
    .catch((error) => new Error(error));
};

/**
 * Manual slot offer (exception to the queue order). The offer holds the
 * slot until the company accepts, rejects or the deadline expires; 409 with
 * a ready-made message when the row is not eligible (PROT-2955).
 */
export const offerWaitlistSlot = (
  id: number,
  data: WaitlistOfferData
): Promise<WaitlistOfferResponse> => {
  const requestOptions = {
    method: 'POST',
    headers: myHeadersWithTokenContentType(),
    body: JSON.stringify(data),
  };
  const baseUrl = handleUrl();
  return fetch(
    `${baseUrl}/api/V1/public-notices/${id}/waitlist/offer`,
    requestOptions
  )
    .then((response) => response.json())
    .catch((error) => new Error(error));
};

export const getCityCoverage = async (
  uf: number
): Promise<CityLocationResponse> => {
  const requestOptions = {
    method: 'GET',
    headers: myHeadersWithTokenContentType(),
  };
  const baseUrl = handleUrl();
  return fetch(`${baseUrl}/api/V1/cities?uf=${uf}&all=true`, requestOptions)
    .then((response) => response.json())
    .catch((error) => new Error(error));
};
